import type { ReactNode } from 'react';
import styles from './Banner.module.css';

/**
 * **`Banner`**（§5.3）。
 *
 * **`Notice` / `OutOfScopeCard` / `ErrorBanner` はこれを土台に組み直せるが、
 * 責務は各ドメイン側に残す**（固定文言 / 変換案つきの断り / 再取得導線）。
 * **この部品は「囲って読ませる」だけ**である。
 *
 * ## `info` を出していない
 *
 * `--ua-bg-information` / `--ua-text-information` が **`ua-tokens.css` に無い**。
 * **ブランド色で代用すると「navy = 情報」という意味が生まれ**、
 * §2.3 が分離した 2 つの青の役割にもう 1 つが混ざる。
 * **無彩色の `neutral` を出す**（何の意味も主張しない）。
 *
 * ## 読み上げ
 *
 * **`danger` だけ `role="alert"`** — それ以外を alert にすると
 * **画面に出るたびに読み上げへ割り込む**。
 * 注意書き（D13 の 2 点）は常に見えている静的な文なので、割り込ませない。
 */
export interface BannerProps {
  readonly variant?: 'neutral' | 'success' | 'warning' | 'danger';
  /** 見出しやアイコンなど、本文の前に置くもの */
  readonly leading?: ReactNode;
  readonly children: ReactNode;
}

export function Banner({ variant = 'neutral', leading, children }: BannerProps): ReactNode {
  return (
    <div
      className={[styles['banner'], styles[variant]].join(' ')}
      {...(variant === 'danger' ? { role: 'alert' as const } : {})}
    >
      {leading}
      <div className={styles['body']}>{children}</div>
    </div>
  );
}
